import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, skip, switchMap, take, tap } from 'rxjs';

import { Team } from '@ninety/platform/models/_shared/team';
import {
  SEGMENT_EVENT_METADATA,
  SegmentTrackEvent,
} from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { BillingTypes } from '@ninety/platform/models/company/billing-types.enum';
import { CompanyUser } from '@ninety/platform/models/company/company-user';
import { DatadogRumUserSyncService } from '@ninety/platform/services/services/datadog-rum-sync.service';
import { ErrorService } from '@ninety/platform/services/services/error.service';
import { RoleService } from '@ninety/platform/services/services/role.service';
import { SegmentService } from '@ninety/platform/services/services/segment.service';

import { selectCurrentUserAsCompanyUser } from '../../../app-entities/users/users-state.selectors';
import { selectHasTier1Data } from '../../../app-init/app-init.selectors';
import { selectIsLoginUrl } from '../../../route.selectors';
import { selectSegmentStripeTraitsModel } from '../../billing/billing-state.selectors';
import { SubscriptionActions } from '../../company/subscription/subscription-state.actions';
import { GainsightPXService } from '../gainsight/gainsight-px.service';
import { type IGainsightGlobalContext, NinetyPlatform } from '../gainsight/models/gainsight-global-context';

import { SegmentActions } from './segment.actions';
import { selectSegmentContactTraits, selectSegmentGroupTraits } from './segment.selectors';

// Not sure why this isn't being picked up by our tsconfig.app.json compilerOptions.types definition
// @ts-ignore
declare const analytics: SegmentAnalytics.AnalyticsJS;

@Injectable()
export class SegmentEffects {
  private readonly actions$ = inject(Actions);
  private readonly segmentService = inject(SegmentService);
  private readonly gainsightPxService = inject(GainsightPXService);
  private readonly store = inject(Store);
  private readonly errorService = inject(ErrorService);
  private readonly datadogRumUserSync = inject(DatadogRumUserSyncService);

  /** Bridges `ErrorService.notify()` to the Segment track action (services -> state inversion). */
  trackError$ = createEffect(() =>
    this.errorService.errorNotified$.pipe(
      map(({ message, title }) =>
        SegmentActions.track({ event: SegmentTrackEvent.ERROR, params: { message, title, source: 'ErrorService' } })
      )
    )
  );

  /** Bridges `DatadogRumUserSyncService`'s RUM↔FullStory session link to a Segment track action. */
  trackDatadogSessionLink$ = createEffect(() =>
    this.datadogRumUserSync.sessionLinked$.pipe(map(({ event, params }) => SegmentActions.track({ event, params })))
  );

  private readonly currentCompanyUser$ = this.store.select(selectCurrentUserAsCompanyUser).pipe(filter(cu => !!cu));
  private readonly hasTier1Data$ = this.store.select(selectHasTier1Data);

  /**
   * Track navigation events.
   */
  trackNavigation$ = createEffect(
    () => {
      const action = this.actions$.pipe(
        ofType(routerNavigatedAction),
        // initial page event called when is segment first load
        skip(1),
        concatLatestFrom(() => [this.segmentService.loaded(), this.store.select(selectIsLoginUrl)]),
        filter(([_, _loaded, isLoginUrl]) => !isLoginUrl),
        map(([routerEvent, _loaded, _isLoginUrl]) => routerEvent.payload),
        tap(_ => this.segmentService.page())
      );

      return action;
    },
    { dispatch: false }
  );

  /**
   * Initialize user/company information for Segment calls.
   */
  initializeSession$ = createEffect(
    () =>
      // When the company user emits
      combineLatest([this.currentCompanyUser$, this.hasTier1Data$]).pipe(
        filter(([cu, hasTier1Data]) => !!cu?.company && hasTier1Data),
        distinctUntilChanged(isEqual),
        // Set Gainsight Properties
        tap(([cu]) => {
          const payload = this.getCommonTraits(cu);
          this.gainsightPxService.setGlobalContext(payload);
        }),
        // If BillingV3, dispatch a request to update the current subscription configuration
        // Otherwise, just emit
        switchMap(([cu]) => {
          if (cu.company.billingType === BillingTypes.BillingV3) {
            // Dispatch outside the traits stream: inside, the tap fired again once the fetch it
            // triggered populated the traits, costing a redundant subscription refresh + cascade.
            return of(null).pipe(
              tap(() => this.store.dispatch(SubscriptionActions.getUpdatedSubscription())),
              switchMap(() => this.store.select(selectSegmentStripeTraitsModel)),
              filter(props => Object.keys(props).length !== 0),
              take(1)
            );
          } else {
            return of(null);
          }
        }),
        // Create Segment properties from the store
        concatLatestFrom(() => [
          this.store.select(selectSegmentContactTraits),
          this.store.select(selectSegmentGroupTraits),
        ]),
        // Identify + Group Segment calls
        tap(([_, contactTraits, groupTraits]) => {
          this.segmentService.onUserChange(contactTraits, groupTraits);
        })
      ),
    { dispatch: false }
  );

  onReady$ = createEffect(() =>
    // TODO: Could make a "destination ready" observable on the service to encapsulate the "analytics" global
    this.segmentService.loaded().pipe(
      /* We need to wait for segment's destinations to be ready.
       * I'm wrapping it in an observable to wait until the callback is triggered then we can emit
       * There might be a better way to do this...
       * https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#ready
       */
      switchMap(
        _ =>
          new Observable(observer => {
            analytics.ready(() => observer.next(null));
          })
      ),
      map(_ => SegmentActions.ready())
    )
  );

  trackGenericEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.track),
        concatLatestFrom(() => this.currentCompanyUser$),
        tap(([{ event, params }, currentCompanyUser]) => {
          const payload = {
            ...this.getCommonTraits(currentCompanyUser),
            ...params,
            ...(SEGMENT_EVENT_METADATA[event] ?? {}),
          };

          this.segmentService.track(event, payload);
        })
      ),
    { dispatch: false }
  );

  /** For tracking when there is no current user context on pages like login/signup */
  trackAnonymousGenericEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SegmentActions.trackAnonymousEvent),
        tap(({ event, params }) =>
          this.segmentService.track(event, { ...params, ...(SEGMENT_EVENT_METADATA[event] ?? {}) })
        )
      ),
    { dispatch: false }
  );

  /**
   * User data will be overwritten in Gainsight, so we want to save a snapshot
   * of relevant data about the current user/company to the events to
   * enhance filtering capabilities.
   *
   * TODO: Create Segment Track Event Traits selector
   */
  private getCommonTraits(cu: CompanyUser): IGainsightGlobalContext {
    return {
      currentUserId: cu?._id,
      currentUserIsImplementer: cu?.isImplementer ?? false,
      currentUserRoleCode: cu?.roleCode,
      currentUserRoleName: RoleService.roleName(cu?.roleCode),
      currentPersonId: cu?.personId,
      currentUserCompany: cu?.company?._id,
      currentUserCompanyBos: cu?.company?.bos,
      companyName: cu?.company?.name,
      email: cu?.emailAddresses?.[0]?.email,
      url: location.href,
      urlPath: location.pathname,
      isLeadership:
        cu.teams.some((t: Pick<Team, 'teamId'>) => t?.teamId === cu.company?.seniorLeadershipTeamId) ?? false,
      ninetyPlatform: NinetyPlatform.Web,
    } as const;
  }
}
